/**
 * Safe localStorage wrappers — degrade to in-memory storage when localStorage
 * is unavailable (Safari private mode throws, some embedded webviews disable
 * it, quota-exceeded errors). Without this, a single `localStorage.setItem`
 * inside a module-level effect can take the whole app down at boot.
 */

const memoryStore = new Map<string, string>()

function isUsable(): boolean {
  try {
    const probe = '__hudstore_probe__'
    window.localStorage.setItem(probe, probe)
    window.localStorage.removeItem(probe)
    return true
  } catch {
    return false
  }
}

const usable = typeof window !== 'undefined' && isUsable()

export function safeGet(key: string): string | null {
  if (usable) {
    try { return window.localStorage.getItem(key) } catch { /* fall through */ }
  }
  return memoryStore.get(key) ?? null
}

export function safeSet(key: string, value: string): void {
  if (usable) {
    try { window.localStorage.setItem(key, value); return } catch { /* fall through */ }
  }
  memoryStore.set(key, value)
}

export function safeRemove(key: string): void {
  if (usable) {
    try { window.localStorage.removeItem(key); return } catch { /* fall through */ }
  }
  memoryStore.delete(key)
}
